import { Link } from "react-router-dom";

/**
 * Kursos Online — Iconographic vibrant logo.
 * Open book with a glowing knowledge spark, painted with a multi-stop
 * gradient (electric blue → cyan → magenta → amber) for a dynamic,
 * youthful feel. Pairs with bold display typography.
 */
const Logo = ({ size = "md", variant = "dark", asLink = true, className = "" }) => {
  const sizes = {
    sm: { box: "h-7 w-7", text: "text-base" },
    md: { box: "h-9 w-9", text: "text-lg" },
    lg: { box: "h-12 w-12", text: "text-2xl" },
  };
  const s = sizes[size] || sizes.md;
  const textColor = variant === "light" ? "text-white" : "text-zinc-900";
  const dotColor = "text-[#4F46E5]";

  const Mark = (
    <svg
      viewBox="0 0 48 48"
      className={`${s.box} drop-shadow-[0_2px_8px_rgba(79,70,229,0.25)]`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ko-book" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4F46E5" />
          <stop offset="55%" stopColor="#818CF8" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
        <linearGradient id="ko-book-r" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>
        <linearGradient id="ko-spark" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="50%" stopColor="#F472B6" />
          <stop offset="100%" stopColor="#22D3EE" />
        </linearGradient>
        <radialGradient id="ko-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FBBF24" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FBBF24" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Soft glow behind spark */}
      <circle cx="24" cy="14" r="11" fill="url(#ko-glow)" />

      {/* Book — left page */}
      <path
        d="M5 16 C 5 16, 14 13, 23 17 L 23 39 C 14 35, 5 38, 5 38 Z"
        fill="url(#ko-book)"
      />
      {/* Book — right page */}
      <path
        d="M43 16 C 43 16, 34 13, 25 17 L 25 39 C 34 35, 43 38, 43 38 Z"
        fill="url(#ko-book-r)"
      />

      {/* Page lines (white, subtle) */}
      <path d="M9 22 L 19 24" stroke="white" strokeOpacity="0.55" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M9 27 L 19 29" stroke="white" strokeOpacity="0.4" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M29 24 L 39 22" stroke="white" strokeOpacity="0.55" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M29 29 L 39 27" stroke="white" strokeOpacity="0.4" strokeWidth="1.2" strokeLinecap="round" />

      {/* Knowledge spark — 4-point star */}
      <path
        d="M24 4 L 26.2 11.8 L 34 14 L 26.2 16.2 L 24 24 L 21.8 16.2 L 14 14 L 21.8 11.8 Z"
        fill="url(#ko-spark)"
      />
    </svg>
  );

  const content = (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {Mark}
      <span className={`font-display font-black ${s.text} tracking-tight ${textColor} leading-none`}>
        Kursos<span className={dotColor}>·</span>Online
      </span>
    </div>
  );

  if (!asLink) return content;
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center">
      {content}
    </Link>
  );
};

export default Logo;
